import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { BarChart3, Clock } from "lucide-react";

interface WeekDayData {
  date: string;
  totalMinutes: number;
  billableMinutes: number;
  matterCount: number;
}

interface TeamMemberData {
  userId: string;
  userName: string;
  weekData: WeekDayData[];
}

interface WorkAllocationWidgetProps {
  data: TeamMemberData[];
}

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const formatHoursDecimal = (mins: number) => (mins / 60).toFixed(1);

/** Generate Mon-Sun date labels for the current week */
const getCurrentWeekDates = (): string[] => {
  const now = new Date();
  const dayOfWeek = now.getDay(); // 0=Sun, 1=Mon, ...
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const monday = new Date(now);
  monday.setDate(now.getDate() + mondayOffset);

  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  });
};

const getCellColor = (totalMinutes: number): string => {
  if (totalMinutes >= 360) return "bg-emerald-100";
  if (totalMinutes >= 180) return "bg-amber-100";
  return "bg-slate-50";
};

const getCellTextColor = (totalMinutes: number): string => {
  if (totalMinutes >= 360) return "text-emerald-800";
  if (totalMinutes >= 180) return "text-amber-800";
  return "text-slate-400";
};

const WorkAllocationWidget: React.FC<WorkAllocationWidgetProps> = ({
  data,
}) => {
  const weekDates = useMemo(() => getCurrentWeekDates(), []);
  const [tooltip, setTooltip] = useState<{
    visible: boolean;
    x: number;
    y: number;
    content: { total: string; billable: string; matters: number };
  }>({ visible: false, x: 0, y: 0, content: { total: "0", billable: "0", matters: 0 } });

  const handleCellMouseEnter = (
    e: React.MouseEvent<HTMLDivElement>,
    dayData: WeekDayData | undefined
  ) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const parentRect = e.currentTarget.closest("section")?.getBoundingClientRect();
    setTooltip({
      visible: true,
      x: rect.left - (parentRect?.left || 0) + rect.width / 2,
      y: rect.top - (parentRect?.top || 0) - 8,
      content: {
        total: formatHoursDecimal(dayData?.totalMinutes || 0),
        billable: formatHoursDecimal(dayData?.billableMinutes || 0),
        matters: dayData?.matterCount || 0,
      },
    });
  };

  const handleCellMouseLeave = () => {
    setTooltip((prev) => ({ ...prev, visible: false }));
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 relative"
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <BarChart3
          className="h-5 w-5 text-slate-700"
          aria-hidden="true"
        />
        <h2 className="text-lg font-bold" style={{ color: "#1F2937" }}>
          Work Allocation
        </h2>
      </div>

      {/* Body */}
      {!data || data.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div className="h-12 w-12 rounded-full bg-slate-50 flex items-center justify-center mb-3">
            <Clock className="h-6 w-6 text-slate-300" />
          </div>
          <p className="font-medium text-slate-500 text-sm">
            No time entries this week
          </p>
          <p className="text-xs text-slate-400">
            Time entries will appear here once logged
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          {/* Tooltip */}
          {tooltip.visible && (
            <div
              className="absolute z-10 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg shadow-lg pointer-events-none"
              style={{
                left: tooltip.x,
                top: tooltip.y,
                transform: "translate(-50%, -100%)",
              }}
            >
              <p className="font-medium">
                {tooltip.content.total} total hrs
              </p>
              <p>{tooltip.content.billable} billable hrs</p>
              <p>
                {tooltip.content.matters} matter
                {tooltip.content.matters !== 1 ? "s" : ""}
              </p>
            </div>
          )}

          <table className="w-full text-xs">
            <thead>
              <tr>
                <th className="text-left py-2 pr-3 font-semibold text-slate-500 w-28">
                  Team Member
                </th>
                {DAY_LABELS.map((day, i) => (
                  <th
                    key={day}
                    className="py-2 px-1 font-semibold text-slate-500 text-center"
                  >
                    <div>{day}</div>
                    <div className="text-[10px] font-normal text-slate-400">
                      {weekDates[i]}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((member, memberIdx) => (
                <motion.tr
                  key={member.userId}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: memberIdx * 0.05, duration: 0.2 }}
                >
                  <td
                    className="py-1.5 pr-3 font-medium truncate max-w-[112px]"
                    style={{ color: "#1F2937" }}
                    title={member.userName}
                  >
                    {member.userName}
                  </td>
                  {DAY_LABELS.map((_, dayIdx) => {
                    const dayData = member.weekData[dayIdx];
                    const minutes = dayData?.totalMinutes || 0;

                    return (
                      <td key={dayIdx} className="py-1.5 px-1">
                        <div
                          className={`rounded-lg h-9 flex items-center justify-center cursor-default transition-colors ${getCellColor(
                            minutes
                          )}`}
                          onMouseEnter={(e) =>
                            handleCellMouseEnter(e, dayData)
                          }
                          onMouseLeave={handleCellMouseLeave}
                        >
                          <span
                            className={`text-xs font-semibold ${getCellTextColor(
                              minutes
                            )}`}
                          >
                            {minutes > 0
                              ? `${formatHoursDecimal(minutes)}h`
                              : "\u2014"}
                          </span>
                        </div>
                      </td>
                    );
                  })}
                </motion.tr>
              ))}
            </tbody>
          </table>

          {/* Legend */}
          <div className="flex items-center gap-4 mt-4 pt-3 border-t border-slate-100">
            <div className="flex items-center gap-1.5">
              <span className="h-3 w-3 rounded bg-emerald-100 border border-emerald-200" />
              <span className="text-[10px] text-slate-500">6+ hrs</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="h-3 w-3 rounded bg-amber-100 border border-amber-200" />
              <span className="text-[10px] text-slate-500">3-6 hrs</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="h-3 w-3 rounded bg-slate-50 border border-slate-200" />
              <span className="text-[10px] text-slate-500">&lt; 3 hrs</span>
            </div>
          </div>
        </div>
      )}
    </motion.section>
  );
};

export default WorkAllocationWidget;
